<template>
  <div class="consumer-dashboard">
    <!-- Page de confirmation -->
    <div v-if="submitted" class="success-view">
      <div class="success-header">
        <div class="success-icon">✅</div>
        <h2 class="success-title">Merci !</h2>
        <p class="success-subtitle">Ton avis a bien été enregistré</p>
      </div>

      <div class="achievement-card">
        <p class="achievement-icon">⭐</p>
        <p class="achievement-text">Tu as déjà donné</p>
        <p class="achievement-count">{{ totalReviews }} avis !</p>
      </div>

      <!-- Proposition gaspillage -->
      <div class="waste-promo-card">
        <h3 class="waste-promo-title">👀 Veux-tu voir combien on a gaspillé aujourd'hui ?</h3>
        <button @click="showWasteViz = true" class="btn-waste">🗑️ Voir le gaspillage</button>
      </div>

      <button @click="resetForm" class="btn btn-secondary">Donner un nouvel avis</button>
    </div>

    <!-- Formulaire d'avis -->
    <div v-else>
      <!-- Header -->
      <div class="dashboard-header">
        <h1 class="dashboard-title">MON AVIS</h1>
        <p class="dashboard-subtitle">Qu'as-tu pensé du repas ?</p>
      </div>

      <!-- Badge sensibilisation -->
      <div class="eco-badge">
        <div class="eco-icon">🌍</div>
        <div class="eco-text">
          <strong>Sauve la planète !</strong>
          <p>Chaque assiette finie compte</p>
        </div>
      </div>

      <!-- Card: Appréciation -->
      <div class="card">
        <h3 class="card-title">🍽️ Comment était ton repas ?</h3>

        <div class="emoji-grid">
          <button
            v-for="item in emojis"
            :key="item.value"
            @click="rating = item.value"
            :class="{ active: rating === item.value }"
            class="emoji-btn"
          >
            <span class="emoji-large">{{ item.emoji }}</span>
            <span class="emoji-label">{{ item.label }}</span>
          </button>
        </div>

        <div v-if="rating" class="selection-feedback">{{ getSelectedEmoji() }} {{ getSelectedLabel() }}</div>
      </div>

      <!-- Card: Plat préféré -->
      <div class="card">
        <h3 class="card-title">🍕 Qu'est-ce que tu as préféré ?</h3>

        <div class="dishes-list">
          <button
            v-for="item in dishes"
            :key="item.id"
            @click="preferred = item.id"
            :class="{ active: preferred === item.id }"
            class="dish-option"
          >
            <span class="dish-check">{{ preferred === item.id ? "✓" : "○" }}</span>
            <span class="dish-label">{{ item.label }}</span>
          </button>
        </div>
      </div>

      <!-- Card: Commentaire -->
      <div class="card">
        <h3 class="card-title">💭 Un commentaire ?</h3>
        <textarea v-model="comment" placeholder="C'était délicieux !" class="comment-input" rows="3" />
      </div>

      <!-- Bouton d'envoi -->
      <button @click="submitReview" :disabled="!rating" :class="{ disabled: !rating }" class="btn btn-primary">
        <span class="btn-icon">📤</span>
        <span>Envoyer mon avis</span>
      </button>

      <!-- Compteur -->
      <div class="counter-text">
        ⭐ Tu as déjà donné
        <strong>{{ totalReviews }} avis</strong>
        !
      </div>
    </div>

    <!-- Modal fullscreen: Gaspillage -->
    <div v-if="showWasteViz" class="fullscreen-modal">
      <button @click="showWasteViz = false" class="fullscreen-close">✕ Fermer</button>
      <waste-visualization mode="display" />
    </div>
  </div>
</template>

<script>
import WasteVisualization from "@/components/WasteVisualization.vue"

export default {
  name: "ConsumerDashboard2",
  components: {
    WasteVisualization,
  },
  data() {
    return {
      rating: null,
      preferred: "plat",
      comment: "",
      submitted: false,
      showWasteViz: false,
      totalReviews: 23,
      emojis: [
        { value: 5, emoji: "😍", label: "Excellent" },
        { value: 4, emoji: "😊", label: "Très bon" },
        { value: 3, emoji: "😐", label: "Correct" },
        { value: 2, emoji: "😕", label: "Moyen" },
        { value: 1, emoji: "😞", label: "Pas bon" },
      ],
      dishes: [
        { id: "entree", label: "Entrée" },
        { id: "plat", label: "Plat principal" },
        { id: "accomp", label: "Accompagnement" },
        { id: "dessert", label: "Dessert" },
      ],
    }
  },
  methods: {
    getSelectedEmoji() {
      const selected = this.emojis.find((e) => e.value === this.rating)
      return selected ? selected.emoji : ""
    },
    getSelectedLabel() {
      const selected = this.emojis.find((e) => e.value === this.rating)
      return selected ? selected.label : ""
    },
    submitReview() {
      if (this.rating) {
        this.submitted = true
        this.totalReviews++
      }
    },
    resetForm() {
      this.rating = null
      this.preferred = "plat"
      this.comment = ""
      this.submitted = false
    },
  },
}
</script>

<style scoped>
.consumer-dashboard {
  max-width: 600px;
  margin: 0 auto;
  padding: 1.5rem;
  background-color: #f8f9fa;
  min-height: 100vh;
}

/* Success view */
.success-view {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.success-header {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  border-radius: 16px;
  padding: 3rem 2rem;
  text-align: center;
  color: white;
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.3);
}

.success-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.success-title {
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
}

.success-subtitle {
  font-size: 1rem;
  opacity: 0.95;
  margin: 0;
}

.achievement-card {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.achievement-icon {
  font-size: 3rem;
  margin-bottom: 0.5rem;
}

.achievement-text {
  font-size: 1rem;
  color: #6b7280;
  margin: 0 0 0.5rem 0;
}

.achievement-count {
  font-size: 2.5rem;
  font-weight: 700;
  color: #2563eb;
  margin: 0;
}

.waste-promo-card {
  background: white;
  border: 2px solid #f97316;
  border-radius: 16px;
  padding: 1.5rem;
  text-align: center;
}

.waste-promo-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 1rem 0;
}

.btn-waste {
  width: 100%;
  padding: 1rem;
  background: #f97316;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1.125rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-waste:hover {
  background: #ea580c;
  transform: scale(1.02);
}

/* Form view */
.dashboard-header {
  margin-bottom: 2rem;
  text-align: center;
}

.dashboard-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}

.dashboard-subtitle {
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
}

/* Eco badge */
.eco-badge {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 16px;
  padding: 1.5rem;
  color: white;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.eco-icon {
  font-size: 3rem;
}

.eco-text strong {
  display: block;
  font-size: 1.125rem;
  margin-bottom: 0.25rem;
}

.eco-text p {
  font-size: 0.875rem;
  opacity: 0.95;
  margin: 0;
}

/* Cards */
.card {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 1.5rem;
}

.card-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 1.5rem 0;
  text-align: center;
}

/* Emoji grid */
.emoji-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;
}

@media (min-width: 480px) {
  .emoji-grid {
    grid-template-columns: repeat(5, 1fr);
  }
}

.emoji-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 0.5rem;
  background: #f9fafb;
  border: 3px solid transparent;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s;
}

.emoji-btn:hover {
  background: #eff6ff;
}

.emoji-btn.active {
  background: #dbeafe;
  border-color: #2563eb;
  transform: scale(1.05);
}

.emoji-large {
  font-size: 3rem;
}

.emoji-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
  text-align: center;
}

.selection-feedback {
  background: #d1fae5;
  border: 2px solid #22c55e;
  border-radius: 12px;
  padding: 1rem;
  text-align: center;
  font-size: 1.25rem;
  font-weight: 700;
  color: #065f46;
}

/* Dishes list */
.dishes-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.dish-option {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f9fafb;
  border: 3px solid transparent;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
}

.dish-option:hover {
  background: #eff6ff;
}

.dish-option.active {
  background: #dbeafe;
  border-color: #2563eb;
}

.dish-check {
  font-size: 1.5rem;
  color: #2563eb;
}

.dish-label {
  font-size: 1rem;
  font-weight: 500;
  color: #1a1a1a;
}

/* Comment */
.comment-input {
  width: 100%;
  padding: 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 1rem;
  font-family: inherit;
  resize: none;
  transition: border-color 0.2s;
}

.comment-input:focus {
  outline: none;
  border-color: #2563eb;
}

/* Buttons */
.btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1.125rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1.125rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.btn-primary {
  background: #2563eb;
  color: white;
}

.btn-primary:hover:not(.disabled) {
  background: #1d4ed8;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
}

.btn-primary.disabled {
  background: #d1d5db;
  color: #9ca3af;
  cursor: not-allowed;
  box-shadow: none;
}

.btn-secondary {
  background: #f3f4f6;
  color: #1a1a1a;
}

.btn-secondary:hover {
  background: #e5e7eb;
}

.btn-icon {
  font-size: 1.5rem;
}

/* Counter */
.counter-text {
  text-align: center;
  color: #6b7280;
  font-size: 0.95rem;
  margin-top: 1rem;
}

.counter-text strong {
  color: #2563eb;
}

/* Fullscreen modal */
.fullscreen-modal {
  position: fixed;
  inset: 0;
  background: white;
  z-index: 2000;
  overflow-y: auto;
  padding: 1rem;
}

.fullscreen-close {
  position: fixed;
  top: 1rem;
  right: 1rem;
  background: #ef4444;
  color: white;
  border: none;
  padding: 0.875rem 1.5rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  z-index: 2001;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.fullscreen-close:hover {
  background: #dc2626;
}

.menu-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.menu-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 8px;
}

.menu-label {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}

.menu-value {
  font-size: 0.875rem;
  color: #1a1a1a;
  font-weight: 600;
}

@media (max-width: 480px) {
  .consumer-dashboard {
    padding: 1rem;
  }

  .dashboard-title {
    font-size: 1.75rem;
  }

  .success-icon {
    font-size: 3rem;
  }

  .success-title {
    font-size: 1.5rem;
  }

  .emoji-large {
    font-size: 2.5rem;
  }
}
</style>
